import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Clickable from 'components/Common/Clickable'
import LightBoxContext from 'components/Luxkit/Modal/LightBoxContext'
import { ImageCarouselInterstitial } from 'components/Luxkit/Carousel/BaseImageCarousel'
import VideoLightbox from 'components/Luxkit/Carousel/VideoLightbox'
import VimeoVideo from 'components/Common/Video/VimeoVideo'
import LinePlayIcon from 'components/Luxkit/Icons/line/LinePlayIcon'
import ImageGallery from 'components/Luxkit/Carousel/ImageGallery'
import PartnershipLabel from 'components/Luxkit/Label/PartnershipLabel'
import LayoutContainer from 'components/Common/LayoutContainer'
import { VIRGIN_VELOCITY_PARTNERSHIP_CODE } from 'constants/partnerships'

const GalleryContainer = styled(LayoutContainer)`
  padding: 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(24)} ${rem(16)} 0;
  }
`

const PlayButton = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transition: transform 0.2s;
  transform: translateY(-50%) translateX(-50%) scale(1);
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.eight};
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${rem(80)};
  width: ${rem(80)};

  >:first-child {
    height: ${rem(48)};
    width: ${rem(48)};
  }

  ${mediaQueryUp.tablet} {
    height: ${rem(108)};
    width: ${rem(108)};

    >:first-child {
      height: ${rem(60)};
      width: ${rem(60)};
    }
  }
`

const VimeoWrapper = styled(Clickable)`
  height: 100%;
  width: 100%;
  position: relative;
  background-color: ${props => props.theme.palette.neutral.one};

  > * {
    pointer-events: none;
  }

  &:focus, &:hover {
    > ${PlayButton} {
      transform: translateY(-50%) translateX(-50%) scale(1.2);
    }
  }
`

interface Props {
  video: Tours.Video | undefined
  selectedVariation: Tours.V2OfferVariation | undefined
  partnershipCode: string | undefined
}

function TourV2Gallery(props: Props) {
  const { video, selectedVariation, partnershipCode } = props
  const showLightbox = useContext(LightBoxContext)
  const interstitials: Array<ImageCarouselInterstitial> | undefined = useMemo(() => {
    if (video?.id) {
      return [{
        id: 'video',
        position: 2,
        element: <VimeoWrapper onClick={() => showLightbox(<VideoLightbox videoId={video.id} playOnMount />)}>
          <VimeoVideo vimeoVideoId={video.id} controls={false} />
          <PlayButton>
            <LinePlayIcon />
          </PlayButton>
        </VimeoWrapper>,
      }]
    }
    return undefined
  }, [video, showLightbox])

  const isVelocityEnabled = partnershipCode === VIRGIN_VELOCITY_PARTNERSHIP_CODE

  return <GalleryContainer>
    <ImageGallery
      videoId={video?.id}
      images={selectedVariation?.images}
      arrowBehaviour="always-visible"
      interstitials={interstitials}
      topLeft={isVelocityEnabled && <PartnershipLabel canEarn canUse variant="icon"/>}
    />
  </GalleryContainer>
}

export default TourV2Gallery
